import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useContext, useEffect, useState, useCallback } from "react";
import { DashboardContext } from "../../../providers/Dashboard";
import { desmembrarUnidade } from "../../../services/api";
import { getAll } from "../../../services/db";
import toast from "react-hot-toast";
import { getSituacaoIptu } from "../../../services/api";
import { apiGateway } from "../../../repositories/api.gateway";
import _ from "lodash";
import { formatarCpfCnpj } from "../../../utils/formataCpfCnpj";
import { usaSituacaoIptu, usaTelheiroGalpaoNoSplit } from "../../../constants/municipios";

interface Props {
  municipio: string;
  openDialog: boolean;
  handleCloseDialog: () => void;
  fetchAndUpdateDashboardData: (id?: string, parameter?: string) => Promise<void>;
}

export function DialogSplitImobiliario({ municipio, openDialog, handleCloseDialog, fetchAndUpdateDashboardData }: Props) {
  const { metadata, setMetadata } = useContext(DashboardContext);
  const [valueTamanho, setValueTamanho] = useState(0);
  const [valueNumUnidade, setValueNumUnidade] = useState("0");
  const [valueAreaTelheiro, setValueAreaTelheiro] = useState<number | null>(null);
  const [valueAreaGalpao, setValueAreaGalpao] = useState<number | null>(null);
  const [dataUnidade, setDataUnidade] = useState(null);
  const [pessoaSelecionada, setPessoaSelecionada] = useState<string | null>(null);
  const [pessoas, setPessoas] = useState<any[]>([]);
  const [isLoadingPessoas, setIsLoadingPessoas] = useState(false);
  const [situacaoIptu, setSituacaoIptu] = useState<string | null>(null);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [openMainDialog, setOpenMainDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [numeroUnidadeTouched, setNumeroUnidadeTouched] = useState(false);
  const [areaTouched, setAreaTouched] = useState(false);
  const [telheiroTouched, setTelheiroTouched] = useState(false);
  const [galpaoTouched, setGalpaoTouched] = useState(false);

  const isVitoriaDaConquista = usaTelheiroGalpaoNoSplit(municipio);
  const isTelheiroDisabled =
    isVitoriaDaConquista &&
    (dataUnidade?.area_telheiro == null || Number(dataUnidade?.area_telheiro) === 0);
  const isGalpaoDisabled =
    isVitoriaDaConquista &&
    (dataUnidade?.area_galpao == null || Number(dataUnidade?.area_galpao) === 0);
  const isNumeroUnidadeValido = valueNumUnidade && Number(valueNumUnidade) > 0;
  const isAreaValida = (() => {
    if (typeof valueTamanho !== "number" || Number.isNaN(valueTamanho)) return false;
    return valueTamanho > 0;
  })();
  const isPessoaValida = Boolean(pessoaSelecionada);
  const isTelheiroValido =
    !isVitoriaDaConquista ||
    isTelheiroDisabled ||
    (valueAreaTelheiro != null &&
      !Number.isNaN(valueAreaTelheiro) &&
      valueAreaTelheiro > 0 &&
      (valueTamanho <= 0 || valueAreaTelheiro < valueTamanho));
  const isGalpaoValido =
    !isVitoriaDaConquista ||
    isGalpaoDisabled ||
    (valueAreaGalpao != null &&
      !Number.isNaN(valueAreaGalpao) &&
      valueAreaGalpao > 0 &&
      (valueTamanho <= 0 || valueAreaGalpao < valueTamanho));
  const isFormValido = isVitoriaDaConquista
    ? Boolean(isNumeroUnidadeValido && isAreaValida && isPessoaValida && isTelheiroValido && isGalpaoValido)
    : Boolean(isNumeroUnidadeValido && isAreaValida && isPessoaValida);
  const numeroUnidadeErro = numeroUnidadeTouched && !isNumeroUnidadeValido;
  const areaErro = areaTouched && !isAreaValida;
  const telheiroErro =
    telheiroTouched &&
    isVitoriaDaConquista &&
    !isTelheiroDisabled &&
    !isTelheiroValido;
  const galpaoErro =
    galpaoTouched &&
    isVitoriaDaConquista &&
    !isGalpaoDisabled &&
    !isGalpaoValido;

  function handleCloseDialogs() {
    if (isProcessing) return;
    setOpenWarningDialog(false);
    setOpenMainDialog(false);
    handleCloseDialog();
  }  

  useEffect(() => {
    const situacoesPermitidas = ["Não consta", "Imune", "Isento", "Cancelado", "Pago"];

    const fetchSituacaoIptu = async () => {
      try {
        let data = []; 
        
        if (usaSituacaoIptu(municipio)) {
          data = await getSituacaoIptu({
            municipio,
            id_imobiliario: [Number(metadata.id_imobiliario)],
          });
        }

        if (data.length) {
          setSituacaoIptu(data[0].situacao_iptu)
          if (situacoesPermitidas.includes(data[0].situacao_iptu)) {
            setOpenMainDialog(true);
          } else {
            setOpenWarningDialog(true);
          }
        } else {
          setOpenMainDialog(true);
        }
      } catch (error) {
        toast.error("Erro ao buscar a situação do IPTU.")
      }
    };

    if (openDialog) {
      fetchSituacaoIptu();
    }
  }, [openDialog, metadata.id_imobiliario]);

  const getDadosFromPessoa = useCallback(async (field: string, isIdPessoa: boolean = false) => {
    try {
      let response;

      if (isIdPessoa) {
        response = await apiGateway.get(`/rest/pessoa?id=eq.${field}`);
        return [response.data];
      }

      const nomeCpf = field.length >= 3 ? field : "default";
      response = await apiGateway.get(
        `/rest/pessoa?or=(nome.ilike.*${nomeCpf}*,cpf_cnpj.ilike.*${nomeCpf}*,codigo.ilike.*${nomeCpf}*)&limit=20`,
      );
      return response.data;
    } catch (error: any) {
      toast.error(`Erro ao buscar pessoas`);
      console.error(error.message);
      throw new Error(error.message);
    }
  }, []);

  const debouncedSearch = useCallback(
    _.debounce(async (field) => {
      setIsLoadingPessoas(true);
      const result = await getDadosFromPessoa(field);
      setPessoas(result || []);
      setIsLoadingPessoas(false);
    }, 500),
    [getDadosFromPessoa],
  );

  useEffect(() => {
    const fetchPessoaVinculada = async () => {
      try {
        const unidade = getAll("imobiliario")?.find((unidade) => unidade.id === Number(metadata.id_imobiliario));

        setDataUnidade(unidade);

        const vinculo = (getAll("imobiliario_pessoa") || []).find(
          (pessoa) => String(pessoa.id_imobiliario) === String(metadata.id_imobiliario),
        );

        if (vinculo?.id_pessoa) {
          const pessoaDetalhes = await getDadosFromPessoa(vinculo.id_pessoa, true);

          if (pessoaDetalhes && pessoaDetalhes.length > 0) {
            setPessoaSelecionada(pessoaDetalhes[0][0]);
          }
        }
      } catch (error) {
        toast.error("Erro ao carregar dados.");
      }
    };

    if (metadata.id_imobiliario) {
      fetchPessoaVinculada();
    }
  }, [openDialog, getDadosFromPessoa]);

  function handlePessoaInputChange(event: any, value: string) {
    if (value.length >= 3) {
      debouncedSearch(value);
    } else {
      setPessoas([]);
    }
  }

  async function handleSplitImobiliario() {
    const numUnidadeInvalido = !valueNumUnidade || Number(valueNumUnidade) <= 0;
    const areaInvalida = !isAreaValida;
    const pessoaInvalida = !pessoaSelecionada;

    if (numUnidadeInvalido || areaInvalida || pessoaInvalida) {
      toast.error("Preencha todos os campos corretamente.");
      return;
    }

    setIsProcessing(true);

    const codigoPessoa = (pessoaSelecionada as unknown as { codigo: string }).codigo;
    const idPessoa = (pessoaSelecionada as unknown as { id: string }).id;

    if (isVitoriaDaConquista) {
      const telheiroVal = isTelheiroDisabled ? 0 : Number(valueAreaTelheiro);
      const galpaoVal = isGalpaoDisabled ? 0 : Number(valueAreaGalpao);

      if (!isTelheiroDisabled) {
        if (isNaN(telheiroVal) || telheiroVal <= 0 || telheiroVal >= valueTamanho) {
          toast.error("Área do telheiro deve ser > 0 e < área da unidade.");
          return;
        }
      }

      if (!isGalpaoDisabled) {
        if (isNaN(galpaoVal) || galpaoVal <= 0 || galpaoVal >= valueTamanho) {
          toast.error("Área do galpão deve ser > 0 e < área da unidade.");
          return;
        }
      }
    }

    try {
      const resultado =
        isVitoriaDaConquista
          ? await desmembrarUnidade(
              dataUnidade.id,
              valueNumUnidade,
              valueTamanho,
              codigoPessoa,
              idPessoa,
              isTelheiroDisabled ? 0 : Number(valueAreaTelheiro),
              isGalpaoDisabled ? 0 : Number(valueAreaGalpao),
            )
          : await desmembrarUnidade(dataUnidade.id, valueNumUnidade, valueTamanho, codigoPessoa, idPessoa);

      if (resultado.status === "success") {
        setValueNumUnidade("0");
        setValueTamanho(0);
        setValueAreaTelheiro(null);
        setValueAreaGalpao(null);
        setIsProcessing(false);
        handleCloseDialogs();

        await fetchAndUpdateDashboardData(undefined, "SplitMaracanau");

        toast.success("Desmembramento realizado com sucesso!");
      } else {
        setIsProcessing(false);
        toast.error("Falha no desmembramento.");
      }
    } catch (error) {
      console.error("Erro no desmembramento:", error);
      toast.error("Erro ao realizar o desmembramento.");
      setIsProcessing(false);
    }
  }

  return (
    <>
    <Dialog
        open={openWarningDialog}
        disableEscapeKeyDown={isProcessing}
        onClose={(_, reason) => {
          if (isProcessing) return;
          handleCloseDialogs();
        }}
      >
        <DialogContent>
          <Typography variant="h5">
            Aviso
          </Typography>
          <Typography variant="body1" style={{ textAlign: "center", marginTop: "10px" }}>
            O imóvel possui dívida de IPTU em aberto e não pode ser desmembrado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="success" variant="contained" disabled={isProcessing}>
            OK
          </Button>
        </DialogActions>
    </Dialog>

    <Dialog
      open={openMainDialog}
      disableEscapeKeyDown={isProcessing}
      onClose={(_, reason) => {
        if (isProcessing) return;
        handleCloseDialogs();
      }}
      sx={{
        width: "100%",
      }}
    >
      <DialogContent
        sx={{
          width: "100%",
        }}
      >
        <h2 style={{ textAlign: "center", width: "100%" }}>Desmembramento de Imobiliário</h2>

        <TextField
          fullWidth
          variant="filled"
          placeholder="000"
          size="small"
          style={{ marginBottom: "16px" }}
          error={numeroUnidadeErro}
          label={"Digite o número da nova unidade imobiliária"}
          helperText={
            <>
              O número da nova unidade imobiliária corresponde ao final da inscrição cartográfica, por exemplo:
              0102003004
              <b>
                <u>001</u>
              </b>
            </>
          }
          type="number"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: {
              appearance: "none",
              MozAppearance: "textfield",
              color: numeroUnidadeErro ? "#d32f2f" : undefined,
            },
          }}
          onBlur={() => setNumeroUnidadeTouched(true)}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
            setValueNumUnidade(e.target.value)
          }
        />

        <TextField
          fullWidth
          variant="filled"
          label="Digite a área do novo imóvel"
          size="small"
          type="number"
          helperText={<>O valor da área deve ser dado em metros quadrados (m²)</>}
          style={{ marginBottom: "16px" }}
          error={areaErro}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: {
              appearance: "none",
              MozAppearance: "textfield",
              color: areaErro ? "#d32f2f" : undefined,
            },
          }}
          onBlur={() => setAreaTouched(true)}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
            setValueTamanho(Number(e.target.value))
          }
        />

        {isVitoriaDaConquista && (
          <>
            <TextField
              fullWidth
              variant="filled"
              label="Área do telheiro (m²)"
              size="small"
              type="number"
              helperText={
                isTelheiroDisabled ? (
                  <>A unidade original não possui área de telheiro disponível para redistribuição/preenchimento.</>
                ) : (
                  <>Informe a área do telheiro. Deve ser maior que 0 e menor que a área da unidade.</>
                )
              }
              style={{ marginBottom: "16px" }}
              disabled={isTelheiroDisabled}
              error={telheiroErro}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: { appearance: "none", MozAppearance: "textfield" },
                min: 0,
              }}
              onBlur={() => {
                if (!isTelheiroDisabled) setTelheiroTouched(true);
              }}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                setValueAreaTelheiro(e.target.value ? Number(e.target.value) : null)
              }
            />

            <TextField
              fullWidth
              variant="filled"
              label="Área do galpão (m²)"
              size="small"
              type="number"
              helperText={
                isGalpaoDisabled ? (
                  <>A unidade original não possui área de galpão disponível para redistribuição/preenchimento.</>
                ) : (
                  <>Informe a área do galpão. Deve ser maior que 0 e menor que a área da unidade.</>
                )
              }
              style={{ marginBottom: "16px" }}
              disabled={isGalpaoDisabled}
              error={galpaoErro}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: { appearance: "none", MozAppearance: "textfield" },
                min: 0,
              }}
              onBlur={() => {
                if (!isGalpaoDisabled) setGalpaoTouched(true);
              }}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                setValueAreaGalpao(e.target.value ? Number(e.target.value) : null)
              }
            />
          </>
        )}

        <Autocomplete
          options={pessoas}
          getOptionLabel={(option) =>
            `${option.nome || option.nome_legado}`
          }
          onInputChange={handlePessoaInputChange}
          onChange={(event, newValue) => setPessoaSelecionada(newValue)}
          value={pessoaSelecionada}
          loading={isLoadingPessoas}
          noOptionsText="Não existe nenhuma pessoa com o filtro selecionado"
          loadingText="Buscando informações..."
          sx={{ marginTop: "16px" }}
          renderOption={(props, option) => (
            <Box
              {...props}
              component="li"
              sx={{ display: "flex", flexDirection: "column", padding: "8px" }}
            >
              <Typography variant="body1">{option.nome || option.nome_legado}</Typography>
              <Typography variant="body2" color="text.secondary">
                CPF/CNPJ: {formatarCpfCnpj(option.cpf_cnpj)} | Código: {option.codigo}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Selecione o proprietário do novo imobiliário"
              variant="filled"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>

      <DialogActions
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          color="success"
          onClick={handleSplitImobiliario}
          variant="contained"
          sx={{ marginBottom: "2rem" }}
          disabled={isProcessing || !isFormValido}
          startIcon={isProcessing ? <CircularProgress color="inherit" size={20} /> : undefined}
        >
          {isProcessing ? "Realizando desmembramento..." : "Desmembrar Imobiliário"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
